import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Grid, Divider, Typography } from "@material-ui/core";
import Title from "../Wrappers/Title";

function convertSeconds(seconds) {
  const pad = (x) => (x < 10 ? "0" + x : x);
  return `${pad(parseInt(seconds / 3600))}:${pad(parseInt((seconds / 60) % 60))}:${pad(seconds % 60)}`;
}

function convertTimeToSeconds(time) {
  if (!time) return 0;
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
}

function ShowProductivity({ posts = [], agentState, loading }) {
  const chartRef = useRef(null);

  const [freeTime, setFreeTime] = useState(0);
  const [breakTime, setBreakTime] = useState(0);
  const [callTime, setCallTime] = useState(0);
  const [confTime, setConfTime] = useState(0);

  // initialize once from props
  useEffect(() => {
    if (!loading && posts.length) {
      posts.forEach((item) => {
        const sec = convertTimeToSeconds(item.value);
        if (item.name === "Free Time") setFreeTime(sec);
        if (item.name === "Break Time") setBreakTime(sec);
        if (item.name === "Actual Talk Time") setCallTime(sec);
        if (item.name === "Conference Time") setConfTime(sec);
      });
    }
  }, [posts, loading]);

  // tick the correct timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (agentState === "FREE") setFreeTime((p) => p + 1);
      if (agentState === "BREAK") setBreakTime((p) => p + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [agentState]);

  // update chart data imperatively
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const update = (index, value) => {
      const point = chart.series[0].points[index];
      if (point) {
        point.update({
          y: value,
          custom: { extraInformation: convertSeconds(value) },
        }, false); // false = don't redraw yet
      }
    };

    update(0, freeTime);
    update(1, breakTime);
    update(2, callTime);
    update(3, confTime);
    chart.redraw(); // one redraw per tick
  }, [freeTime, breakTime, callTime, confTime]);

  // render only once – stable options object
  const options = React.useMemo(() => ({
    chart: {
      type: "pie",
      height: 200,
      width: 350,
      animation: false,
    },
    title: { text: "" },
    tooltip: {
      headerFormat: "<b>{point.key}</b><br/>",
      pointFormat: "{point.custom.extraInformation}",
    },
    credits: { enabled: false },
    colors: ["#ff6347", "#536DFE", "#FFC260", "#A0A0A0"],
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      labelFormatter: function () {
        return `${this.name}<br/><b>${convertSeconds(this.y)}</b>`;
      },
      itemStyle: {
        color: "#686d88",
        fontSize: "12px",
        fontWeight: "light",
      },
    },
    plotOptions: {
      pie: {
        showInLegend: true,
        innerSize: "60%",
        dataLabels: { enabled: false },
        animation: false, // prevent slice animation
      },
    },
    series: [
      {
        data: [
          { name: "Free Time", y: 0, custom: { extraInformation: "00:00:00" } },
          { name: "Break Time", y: 0, custom: { extraInformation: "00:00:00" } },
          { name: "Actual Talk Time", y: 0, custom: { extraInformation: "00:00:00" } },
          { name: "Conference Time", y: 0, custom: { extraInformation: "00:00:00" } },
        ],
      },
    ],
  }), []); // options never recreated

  return (
    <React.Fragment>
      <Title>Agent Productivity</Title>
      <Divider />
      <Grid container spacing={2} style={{ marginTop: "15px" }}>
        <Grid item xs={12}>
          {parseInt(posts[0]?.value) > 0 ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              ref={chartRef}
              immutable={false} 
            />
          ) : (
            <Typography
              variant="h5"
              align="center"
              style={{ marginTop: "60px" }}
            >
              No Data Found
            </Typography>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default ShowProductivity;
